import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import { DEFAULT_ROOM_NAME, HERO_ROOM_NAME, ROOM_IMAGE_LIMIT } from '../utils';
import type { UploadedRoomAssignment } from '../utils';

import ImagesByRoomRow from './Row';
import {
  EMPTY_IMAGE_PUBLIC_IDS,
  areRoomImagePublicIdsByNameEqual,
  buildRoomImagesByKey,
  getRoomImagePublicIdsByName,
  getRoomKey,
  pruneUnavailableRoomImages,
} from './utils';
import type { RoomImagesByKey } from './utils';

interface Props {
  excludedImagePublicIds?: Array<string>;
  hiddenImagePublicIds?: Array<string>;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomName: string, selectedImages: Array<MediaImage>) => void;
  onEditImage: (publicId: string) => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onRoomImagesChange: (roomImagePublicIdsByName: Record<string, Array<string>>) => void;
  onToggleHiddenImage: (publicId: string) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  uploadedRoomAssignments?: Array<UploadedRoomAssignment>;
}

function buildPropertyRoomTypesKey(propertyRoomTypes: Array<PropertyRoomTypeWithImages>) {
  return propertyRoomTypes
    .map((room, index) => {
      const imagePublicIds = (room.images ?? []).map((image) => image.id_cloudinary_external).join(',');

      return [getRoomKey(room, index), room.name, imagePublicIds].join(':');
    })
    .join('|');
}

export default function ImagesByRoom({
  excludedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  hiddenImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  mediaImages,
  onAssignImagesToRoom,
  onEditImage,
  onRemoveImageFromRoom,
  onRoomImagesChange,
  onToggleHiddenImage,
  propertyRoomTypes = [],
  uploadedRoomAssignments = [],
}: Props) {
  const [roomImagesByKey, setRoomImagesByKey] = useState<RoomImagesByKey>(() =>
    buildInitialRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, mediaImages),
  );
  const dismissedUploadedRoomAssignmentPublicIds = useRef<Set<string>>(new Set());
  const propertyRoomTypesKey = useMemo(() => buildPropertyRoomTypesKey(propertyRoomTypes), [propertyRoomTypes]);
  const previousPropertyRoomTypesKey = useRef(propertyRoomTypesKey);
  const previousRoomImagePublicIdsByName = useRef<Record<string, Array<string>> | undefined>(undefined);

  const dismissUploadedRoomAssignments = useCallback((publicIds: Array<string>) => {
    if (publicIds.length === 0) {
      return;
    }

    dismissedUploadedRoomAssignmentPublicIds.current = new Set([
      ...dismissedUploadedRoomAssignmentPublicIds.current,
      ...publicIds,
    ]);
  }, []);

  useEffect(() => {
    const hasRoomSourceChanged = previousPropertyRoomTypesKey.current !== propertyRoomTypesKey;

    if (hasRoomSourceChanged) {
      previousPropertyRoomTypesKey.current = propertyRoomTypesKey;
      dismissedUploadedRoomAssignmentPublicIds.current = new Set();
      setRoomImagesByKey(buildInitialRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, mediaImages));
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => pruneUnavailableRoomImages(currentRoomImagesByKey, mediaImages));
  }, [excludedImagePublicIds, mediaImages, propertyRoomTypes, propertyRoomTypesKey]);

  useEffect(() => {
    const nextRoomImagePublicIdsByName = getRoomImagePublicIdsByName(propertyRoomTypes, roomImagesByKey);

    if (
      previousRoomImagePublicIdsByName.current &&
      areRoomImagePublicIdsByNameEqual(previousRoomImagePublicIdsByName.current, nextRoomImagePublicIdsByName)
    ) {
      return;
    }

    previousRoomImagePublicIdsByName.current = nextRoomImagePublicIdsByName;
    onRoomImagesChange(nextRoomImagePublicIdsByName);
  }, [onRoomImagesChange, propertyRoomTypes, roomImagesByKey]);

  useEffect(() => {
    if (uploadedRoomAssignments.length === 0) {
      return;
    }

    const activeUploadedRoomAssignments = uploadedRoomAssignments.filter(
      (assignment) => !dismissedUploadedRoomAssignmentPublicIds.current.has(assignment.publicId),
    );

    if (activeUploadedRoomAssignments.length === 0) {
      return;
    }

    const mediaImagesByPublicId = new Map(mediaImages.map((image) => [image.publicId, image]));

    setRoomImagesByKey((currentRoomImagesByKey) => {
      let nextRoomImagesByKey = currentRoomImagesByKey;
      const processedPublicIds: Array<string> = [];

      activeUploadedRoomAssignments.forEach((assignment) => {
        const mediaImage = mediaImagesByPublicId.get(assignment.publicId);

        if (!mediaImage) {
          return;
        }

        processedPublicIds.push(assignment.publicId);

        const currentRoomImages = nextRoomImagesByKey[assignment.roomKey] ?? [];

        if (
          currentRoomImages.length >= ROOM_IMAGE_LIMIT ||
          currentRoomImages.some((image) => image.publicId === assignment.publicId)
        ) {
          return;
        }

        const selectedPublicIds = new Set([assignment.publicId]);
        const nextRoomImagesWithoutSelection = Object.fromEntries(
          Object.entries(nextRoomImagesByKey).map(([roomKey, roomImages]) => [
            roomKey,
            roomKey === assignment.roomKey
              ? roomImages
              : roomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
          ]),
        );

        nextRoomImagesByKey = {
          ...nextRoomImagesWithoutSelection,
          [assignment.roomKey]: currentRoomImages.concat({
            publicId: mediaImage.publicId,
            title: mediaImage.title,
          }),
        };
      });

      dismissUploadedRoomAssignments(processedPublicIds);

      return nextRoomImagesByKey;
    });
  }, [dismissUploadedRoomAssignments, mediaImages, uploadedRoomAssignments]);

  useEffect(() => {
    const roomKeyByName = new Map(
      propertyRoomTypes
        .map((room, index) => (room.name ? ([room.name, getRoomKey(room, index)] as const) : undefined))
        .filter((roomEntry): roomEntry is readonly [string, string] => !!roomEntry),
    );

    if (roomKeyByName.size === 0) {
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) =>
      syncRoomImagesWithMediaLabels(currentRoomImagesByKey, mediaImages, roomKeyByName),
    );
  }, [mediaImages, propertyRoomTypes]);

  const handleAssignImagesToRoom = (roomName: string, selectedImages: Array<MediaImage>) => {
    dismissUploadedRoomAssignments(selectedImages.map((image) => image.publicId));
    onAssignImagesToRoom(roomName, selectedImages);
  };

  const handleRemoveImageFromRoom = (publicId: string) => {
    if (uploadedRoomAssignments.some((assignment) => assignment.publicId === publicId)) {
      dismissUploadedRoomAssignments([publicId]);
    }

    onRemoveImageFromRoom(publicId);
  };

  if (propertyRoomTypes.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ px: 3, py: 2.5 }}
      >
        <Typography variant="h5">Images by room</Typography>
        <Typography variant="body2" color="text.secondary">
          Each row matches a room in{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Rooms
          </Box>
          . Up to {ROOM_IMAGE_LIMIT} carousel slots.
        </Typography>
      </Stack>
      <Divider />

      <Stack divider={<Divider />} sx={{ px: 3 }}>
        {propertyRoomTypes.map((room, index) => {
          const roomKey = getRoomKey(room, index);

          return (
            <ImagesByRoomRow
              key={roomKey}
              hiddenImagePublicIds={hiddenImagePublicIds}
              mediaImages={mediaImages}
              onAssignImagesToRoom={handleAssignImagesToRoom}
              onEditImage={onEditImage}
              onRemoveImageFromRoom={handleRemoveImageFromRoom}
              onToggleHiddenImage={onToggleHiddenImage}
              room={room}
              roomImages={roomImagesByKey[roomKey] ?? []}
              roomKey={roomKey}
              setRoomImagesByKey={setRoomImagesByKey}
            />
          );
        })}
      </Stack>
    </Paper>
  );
}

function syncRoomImagesWithMediaLabels(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
  roomKeyByName: Map<string, string>,
) {
  let hasChanged = false;
  let nextRoomImagesByKey = roomImagesByKey;

  mediaImages.forEach((mediaImage) => {
    const targetRoomKey = roomKeyByName.get(mediaImage.sourceLabel);
    const isUnassigned = mediaImage.sourceLabel === DEFAULT_ROOM_NAME || mediaImage.sourceLabel === HERO_ROOM_NAME;
    const existingRoomKey = Object.entries(nextRoomImagesByKey).find(([_roomKey, roomImages]) =>
      roomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId),
    )?.[0];

    if (!targetRoomKey && !isUnassigned) {
      return;
    }

    if (isUnassigned) {
      if (!existingRoomKey) {
        return;
      }

      nextRoomImagesByKey = removeRoomImage(nextRoomImagesByKey, mediaImage.publicId);
      hasChanged = true;
      return;
    }

    const targetRoomImages = nextRoomImagesByKey[targetRoomKey] ?? [];
    const isAlreadyInTargetRoom = targetRoomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId);

    if (!isAlreadyInTargetRoom && targetRoomImages.length >= ROOM_IMAGE_LIMIT) {
      return;
    }

    const nextRoomImage = {
      publicId: mediaImage.publicId,
      title: mediaImage.title,
    };

    if (isAlreadyInTargetRoom) {
      const nextTargetRoomImages = targetRoomImages.map((roomImage) =>
        roomImage.publicId === mediaImage.publicId ? nextRoomImage : roomImage,
      );

      if (nextTargetRoomImages === targetRoomImages || areRoomImagesEqual(targetRoomImages, nextTargetRoomImages)) {
        return;
      }

      nextRoomImagesByKey = {
        ...nextRoomImagesByKey,
        [targetRoomKey]: nextTargetRoomImages,
      };
      hasChanged = true;
      return;
    }

    nextRoomImagesByKey = {
      ...removeRoomImage(nextRoomImagesByKey, mediaImage.publicId),
      [targetRoomKey]: targetRoomImages.concat(nextRoomImage),
    };
    hasChanged = true;
  });

  return hasChanged ? nextRoomImagesByKey : roomImagesByKey;
}

function removeRoomImage(roomImagesByKey: RoomImagesByKey, publicId: string) {
  return Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages.filter((roomImage) => roomImage.publicId !== publicId),
    ]),
  );
}

function areRoomImagesEqual(
  firstRoomImages: Array<{ publicId: string; title?: string }>,
  secondRoomImages: Array<{ publicId: string; title?: string }>,
) {
  return (
    firstRoomImages.length === secondRoomImages.length &&
    firstRoomImages.every(
      (firstRoomImage, index) =>
        firstRoomImage.publicId === secondRoomImages[index].publicId &&
        firstRoomImage.title === secondRoomImages[index].title,
    )
  );
}

function buildInitialRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
  mediaImages: Array<MediaImage>,
) {
  return pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages);
}
